// Handing a question to ECONIC full screen (WP-SUPER-AGENT): ⌘K's "Ask ECONIC…", the phone's Ask bar, the ECONIC landing's box and the
// sidebar's "Open full screen" carry the person to /agent with what they were doing — the words typed, the files chosen (the landing's +)
// and the screen they were on (its published state: title, route, department, the widgets in view). The first message of the new
// conversation goes out with that screen as its surface and those files attached, so the agent answers about what the person was looking
// at; the hero names where they came from.
import type { NavigateFunction } from "react-router";
import type { SurfaceState } from "@ncp/assistant-sdk";

export interface AgentHandoff {
  /** The question to send at once (empty: the composer opens, focused). */
  text?: string | null;
  /** The screen the person was on. */
  surface?: SurfaceState | null;
  /** The composer's directive for the first question ("research; sources=kb,data": Today's Research widget starts a Research turn). */
  directive?: string | null;
  /** Hand-off query parameters for /agent (`sources=kb,data,web`: the knowledge sources the person chose). */
  params?: Record<string, string> | null;
  /**
   * Files to go with the first question (the ECONIC landing's +): uploaded to the new conversation, then the question is sent with them.
   * Files without words open the conversation with them waiting in the composer.
   */
  files?: File[] | null;
}

let pending: { id: number; handoff: AgentHandoff } | null = null;
let nextId = 1;

/** Opens a new conversation on /agent carrying the text and the screen (a conversation already open in the sidebar opens itself instead). */
export function handOffToAgent(navigate: NavigateFunction, handoff: AgentHandoff, threadId?: string | null): void {
  pending = { id: nextId++, handoff: threadId ? { surface: handoff.surface ?? null } : handoff };
  const query = !threadId && handoff.params ? new URLSearchParams(handoff.params).toString() : "";
  navigate(threadId ? `/agent/${threadId}` : query ? `/agent?${query}` : "/agent");
}

/**
 * The handoff waiting for the agent screen, without taking it: React may run a state initialiser twice (strict mode), so the screen
 * reads it here and releases it with `releaseHandoff(id)` once it has adopted it. Each handoff has its own id, so it is adopted once.
 */
export function peekHandoff(): { id: number; handoff: AgentHandoff } | null {
  return pending;
}

/** Drops the handoff the screen has adopted (a newer one, made meanwhile, is kept). */
export function releaseHandoff(id: number): void {
  if (pending?.id === id) pending = null;
}

/**
 * The agent screen's place in the history (MoET review 16 Oct 2026): `entry` is the location key on screen, `baseline` the one a new
 * conversation was started on, `stripping` set while the screen takes its own hand-off parameters (`?sources=`, `?q=` …) out of the URL.
 * That clean-up is a replace with a new key but not a newer entry: the baseline moves with it, so the conversation still replaces /agent
 * and Back from it reaches "/" in one step. A newer entry meanwhile (⌘K on /agent) is kept: the conversation is pushed after it.
 */
export interface EntryTrack { entry: string; baseline: string | null; stripping: boolean }

/** The track after the location became `key` by a navigation of `type` (react-router's PUSH | REPLACE | POP). */
export function trackEntry(s: EntryTrack, key: string, type: string): EntryTrack {
  if (s.entry === key) return s;
  const own = s.stripping && type === "REPLACE";
  return { entry: key, baseline: own && s.baseline === s.entry ? key : s.baseline, stripping: own ? false : s.stripping };
}

/** Whether the new conversation replaces the entry on screen: the one it was started on (or that entry after its own clean-up). */
export const replacesEntry = (s: EntryTrack): boolean => s.baseline !== null && s.entry === s.baseline;

/** The first question that waits for its files: sent once they have gone up (or at once when the conversation takes no files). */
export interface FilesThenAsk { id: string; text: string; directive: string | null }

/**
 * Whether the question handed over with files goes now: once the conversation has loaded, the files have been handed to it and none is
 * still uploading or awaiting retry/removal after a failure, or at once when the conversation takes no files.
 */
export function filesThenAskReady(s: { loading: boolean; streaming: boolean; waitingFiles: number; canAttach: boolean; uploading: number; failed: number }): boolean {
  if (s.loading || s.streaming || s.failed > 0) return false;
  if (s.waitingFiles > 0) return !s.canAttach;
  return s.uploading === 0;
}
